import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileText, Upload, Search, X, Plus } from 'lucide-react';
import api from '../../services/api';
import { templateAPI } from '../../services/templateApi';
import { apriFile } from '../../services/fileUrl';
import FirmaConsenso from './FirmaConsenso';
import '../../styles/DocumentiProcedura.css';

/**
 * I due riquadri dei documenti nella schermata di una procedura.
 *
 * PERCHE' PARTONO DALL'ELENCO DEL PAZIENTE
 * I documenti si chiedono con la rotta del paziente e si filtrano per
 * procedura. Una rotta «documenti di questa visita» dovrebbe rifare da
 * capo il filtro della riservatezza estetica, e su questo gestionale una
 * regola scritta in due posti e' gia' costata una fuga di dati: il punto
 * in cui si decide chi vede cosa resta uno solo.
 */

const dataOra = (v) => {
    if (!v) return '';
    const d = new Date(v);
    return isNaN(d) ? '' : d.toLocaleDateString('it-IT') + ' · '
        + d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
};

const peso = (b) => (b ? `${Math.max(1, Math.round(b / 1024))} KB` : '');

/* ------------------------------------------------------------------ */
/*  Documenti generati: i consensi nati da un modello                  */
/* ------------------------------------------------------------------ */

export const DocumentiGenerati = ({ visita }) => {
    const [documenti, setDocumenti] = useState([]);
    const [modelli, setModelli] = useState([]);
    const [cerca, setCerca] = useState('');
    const [apertoElenco, setApertoElenco] = useState(false);
    const [daFirmare, setDaFirmare] = useState(null);
    const [errore, setErrore] = useState(null);
    const contenitore = useRef(null);

    const pazienteId = visita?.paziente_id;
    const visitaId = visita?.id;

    const carica = useCallback(async () => {
        if (!pazienteId) return;
        try {
            const r = await api.get(`/modulistica/paziente/${pazienteId}`);
            const tutti = r.data?.data?.documenti || [];
            setDocumenti(tutti.filter((d) => Number(d.visita_id) === Number(visitaId)));
            setErrore(null);
        } catch (e) {
            setErrore('Non riesco a leggere i documenti');
        }
    }, [pazienteId, visitaId]);

    useEffect(() => { carica(); }, [carica]);

    useEffect(() => {
        templateAPI.getAll()
            .then((r) => setModelli(r.data?.data?.template || []))
            .catch(() => setModelli([]));
    }, []);

    /* Cliccare fuori chiude l'elenco: un menu che resta aperto sopra il
       resto della procedura copre proprio quello che si stava guardando. */
    useEffect(() => {
        if (!apertoElenco) return undefined;
        const fuori = (e) => {
            if (contenitore.current && !contenitore.current.contains(e.target)) {
                setApertoElenco(false);
            }
        };
        document.addEventListener('mousedown', fuori);
        return () => document.removeEventListener('mousedown', fuori);
    }, [apertoElenco]);

    const q = cerca.trim().toLowerCase();
    const trovati = q
        ? modelli.filter((m) => `${m.titolo} ${m.categoria || ''}`.toLowerCase().includes(q))
        : modelli;

    const scegli = (m) => {
        setApertoElenco(false);
        setCerca('');
        setDaFirmare(m);
    };

    return (
        <div className="dp-blocco">
            <div className="dp-azione" ref={contenitore}>
                <button type="button" className="dp-btn" onClick={() => setApertoElenco((v) => !v)}>
                    <Plus size={14} aria-hidden="true" /> Genera documento
                </button>

                {apertoElenco && (
                    <div className="dp-menu">
                        <label className="dp-cerca">
                            <Search size={14} aria-hidden="true" />
                            <input autoFocus placeholder="Cerca un modello" value={cerca}
                                   onChange={(e) => setCerca(e.target.value)} />
                            {cerca && (
                                <button type="button" onClick={() => setCerca('')} aria-label="Togli la ricerca">
                                    <X size={13} />
                                </button>
                            )}
                        </label>
                        <div className="dp-menu-voci">
                            {modelli.length === 0 ? (
                                <p className="dp-vuoto-menu">
                                    Nessun modello di consenso disponibile. Si compongono da
                                    Documenti / Consensi.
                                </p>
                            ) : trovati.length === 0 ? (
                                <p className="dp-vuoto-menu">Nessun modello con questo nome.</p>
                            ) : trovati.map((m) => (
                                <button type="button" key={m.id} className="dp-voce"
                                        onClick={() => scegli(m)}>
                                    <b>{m.titolo}</b>
                                    <span>
                                        {m.categoria || 'senza categoria'}
                                        {m.medico_cognome ? ` · Dr. ${m.medico_cognome}` : ' · della struttura'}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {errore && <p className="dp-errore">{errore}</p>}

            {documenti.length === 0 ? (
                <p className="dp-vuoto">
                    Nessun documento generato per questa procedura. Con <b>Genera
                    documento</b> si sceglie un modello, si fa firmare e resta legato
                    a questa prestazione.
                </p>
            ) : (
                <ul className="dp-elenco">
                    {documenti.map((d) => (
                        <li key={d.id}>
                            <button type="button" className="dp-file" onClick={() => apriFile(d.url)}>
                                <FileText size={15} aria-hidden="true" />
                                <span className="dp-file-nome">{d.titolo || d.tipo}</span>
                            </button>
                            <span className="dp-file-meta">
                                {d.firmato_il ? `firmato ${dataOra(d.firmato_il)}` : dataOra(d.created_at)}
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            {daFirmare && (
                <FirmaConsenso
                    pazienteId={pazienteId}
                    visitaId={visitaId}
                    medicoId={visita?.medico_id}
                    consensi={[{ ...daFirmare, _prestazione: visita?.trattamento || null }]}
                    onClose={() => setDaFirmare(null)}
                    onComplete={() => { setDaFirmare(null); carica(); }}
                />
            )}
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  Documenti caricati: i file che arrivano da fuori                   */
/* ------------------------------------------------------------------ */

/* Passano dalla stessa rotta delle fotografie di procedura, con
   `visita_id`: e' la strada che gia' applica la riservatezza estetica,
   registra l'accesso e manda il file nel cestino invece di cancellarlo.
   Una rotta nuova per i documenti avrebbe dovuto rifare tutte e tre le
   cose, e la terza si sarebbe scoperta mancante il giorno sbagliato. */
export const DocumentiCaricati = ({ visita }) => {
    const [file, setFile] = useState([]);
    const [caricando, setCaricando] = useState(false);
    const [errore, setErrore] = useState(null);
    const scelta = useRef(null);

    const pazienteId = visita?.paziente_id;
    const visitaId = visita?.id;

    const carica = useCallback(async () => {
        if (!pazienteId) return;
        try {
            const r = await api.get(`/foto/paziente/${pazienteId}`);
            const tutti = r.data?.data?.foto || [];
            setFile(tutti.filter((f) => Number(f.visita_id) === Number(visitaId)
                                     && f.categoria === 'documento'));
            setErrore(null);
        } catch (e) {
            setErrore('Non riesco a leggere i documenti caricati');
        }
    }, [pazienteId, visitaId]);

    useEffect(() => { carica(); }, [carica]);

    const invia = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        setCaricando(true);
        setErrore(null);
        try {
            const corpo = new FormData();
            corpo.append('file', f);
            corpo.append('visita_id', visitaId);
            await api.post(`/foto/paziente/${pazienteId}`, corpo,
                           { headers: { 'Content-Type': 'multipart/form-data' } });
            await carica();
        } catch (err) {
            setErrore(err?.message || 'Caricamento non riuscito');
        } finally {
            setCaricando(false);
        }
    };

    return (
        <div className="dp-blocco">
            <div className="dp-azione">
                <button type="button" className="dp-btn" disabled={caricando}
                        onClick={() => scelta.current?.click()}>
                    <Upload size={14} aria-hidden="true" />
                    {caricando ? 'Carico…' : 'Carica documento'}
                </button>
                <input ref={scelta} type="file" hidden onChange={invia}
                       accept=".pdf,.doc,.docx,.txt,.rtf,.odt,image/*" />
            </div>

            {errore && <p className="dp-errore">{errore}</p>}

            {file.length === 0 ? (
                <p className="dp-vuoto">
                    Nessun documento caricato su questa procedura. Referti e
                    documenti che arrivano da fuori si aggiungono qui: seguono la
                    riservatezza della procedura come le fotografie.
                </p>
            ) : (
                <ul className="dp-elenco">
                    {file.map((f) => (
                        <li key={f.id}>
                            <button type="button" className="dp-file" onClick={() => apriFile(f.url)}>
                                <FileText size={15} aria-hidden="true" />
                                <span className="dp-file-nome">{f.etichetta || f.nome_file}</span>
                            </button>
                            <span className="dp-file-meta">
                                {peso(f.dimensione)} {dataOra(f.created_at)}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
